import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-50" }
const _hoisted_2 = { class: "bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm" }
const _hoisted_3 = { class: "px-4 sm:px-6 lg:px-8" }
const _hoisted_4 = { class: "flex justify-between items-center h-16" }
const _hoisted_5 = { class: "flex items-center gap-3" }
const _hoisted_6 = { class: "flex items-center gap-4" }
const _hoisted_7 = { class: "text-right" }
const _hoisted_8 = { class: "text-sm font-medium text-[#22282F]" }
const _hoisted_9 = { class: "text-xs text-gray-500" }
const _hoisted_10 = { class: "text-xs text-gray-400" }
const _hoisted_11 = { class: "flex" }
const _hoisted_12 = {
  class: "hidden lg:flex flex-col w-64 shrink-0 bg-white border-r border-gray-200 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto",
  "data-test": "sidebar"
}
const _hoisted_13 = {
  key: 0,
  class: "lg:hidden fixed inset-0 z-40",
  "data-test": "drawer"
}
const _hoisted_14 = { class: "absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-xl flex flex-col overflow-y-auto" }
const _hoisted_15 = { class: "flex items-center justify-between h-16 px-4 border-b border-gray-100 shrink-0" }
const _hoisted_16 = { class: "flex-1 min-w-0" }

import { computed, ref, watch, defineComponent, h, type PropType } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { LogOut, Menu, X, UserCircle } from 'lucide-vue-next'
import { useAuthStore } from '@/store/auth'
import { buildNavGroups, isNavItemActive, type NavGroup } from '@/utils/navigation'


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminLayout',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const user = authStore.user
const menuOpen = ref(false)

const scopeLabel = computed(() => {
  const roleCode = user?.role?.code
  if (!roleCode) return ''
  if (roleCode === 'ADMIN' && !user?.direction) return 'Administration globale'
  if ((roleCode === 'ADMIN' || roleCode === 'DIRECTEUR') && user?.direction?.nom) return user.direction.nom
  if (roleCode === 'AMBASSADEUR' && user?.ambassade?.nom) return user.ambassade.nom
  return ''
})

const groups = computed(() =>
  buildNavGroups({ roleCode: user?.role?.code, canSection: authStore.canSection })
)

// Le tiroir se referme dès qu'on change de page.
watch(() => route.path, () => { menuOpen.value = false })

const handleLogout = async () => {
  await authStore.logout()
  router.push('/')
}

// Contenu du menu, partagé par le panneau fixe et le tiroir.
const linkClass = (active: boolean) => [
  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
  active ? 'bg-[#22282F] text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-[#22282F]',
]

const NavContent = defineComponent({
  props: {
    groups: { type: Array as PropType<NavGroup[]>, required: true },
    path: { type: String, required: true },
  },
  setup(props) {
    return () =>
      h('nav', { class: 'flex flex-col flex-1 px-3 py-4', 'aria-label': 'Navigation principale' }, [
        h(
          'div',
          { class: 'flex-1 space-y-5' },
          props.groups.map((group) =>
            h('div', { key: group.id, 'data-group': group.id }, [
              h('p', { class: 'px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400' }, group.label),
              h(
                'ul',
                { class: 'space-y-0.5' },
                group.items.map((item) =>
                  h('li', { key: item.id }, [
                    h(
                      RouterLink,
                      { to: item.route, class: linkClass(isNavItemActive(props.path, item)), 'data-nav': item.id },
                      () => [h(item.icon, { class: 'w-4 h-4 shrink-0' }), h('span', item.label)]
                    ),
                  ])
                )
              ),
            ])
          )
        ),
        h('div', { class: 'pt-3 mt-4 border-t border-gray-100' }, [
          h(
            RouterLink,
            { to: '/profil', class: linkClass(props.path === '/profil'), 'data-nav': 'profil' },
            () => [h(UserCircle, { class: 'w-4 h-4 shrink-0' }), h('span', 'Mon profil')]
          ),
        ]),
      ])
  },
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("button", {
              type: "button",
              class: "lg:hidden p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors",
              "aria-label": "Ouvrir le menu",
              "data-test": "menu-toggle",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (menuOpen.value = true))
            }, [
              _createVNode(_unref(Menu), { class: "w-5 h-5 text-gray-600" })
            ]),
            _cache[3] || (_cache[3] = _createElementVNode("img", {
              src: "/logo.png",
              alt: "Logo",
              class: "h-10 w-auto"
            }, null, -1))
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(user)?.full_name || _unref(user)?.username), 1),
              _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(user)?.role?.nom), 1),
              _createElementVNode("p", _hoisted_10, _toDisplayString(scopeLabel.value), 1)
            ]),
            _createElementVNode("button", {
              onClick: handleLogout,
              class: "p-2 hover:bg-gray-100 rounded-lg transition-colors",
              title: "Déconnexion"
            }, [
              _createVNode(_unref(LogOut), { class: "w-5 h-5 text-gray-500" })
            ])
          ])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_11, [
      _createElementVNode("aside", _hoisted_12, [
        _createVNode(_unref(NavContent), {
          groups: groups.value,
          path: _ctx.$route.path
        }, null, 8, ["groups", "path"])
      ]),
      (menuOpen.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
            _createElementVNode("div", {
              class: "absolute inset-0 bg-black/40",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (menuOpen.value = false))
            }),
            _createElementVNode("aside", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _cache[4] || (_cache[4] = _createElementVNode("img", {
                  src: "/logo.png",
                  alt: "Logo",
                  class: "h-9 w-auto"
                }, null, -1)),
                _createElementVNode("button", {
                  type: "button",
                  class: "p-2 hover:bg-gray-100 rounded-lg",
                  "aria-label": "Fermer le menu",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (menuOpen.value = false))
                }, [
                  _createVNode(_unref(X), { class: "w-5 h-5 text-gray-500" })
                ])
              ]),
              _createVNode(_unref(NavContent), {
                groups: groups.value,
                path: _ctx.$route.path
              }, null, 8, ["groups", "path"])
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("main", _hoisted_16, [
        _renderSlot(_ctx.$slots, "default")
      ])
    ])
  ]))
}
}

})